import React from 'react';
import { type ApolloQueryResult } from '@apollo/react-hooks';
import ErrorIcon from '@mui/icons-material/Error';
import { FUNCTION_MODE } from './ManageChiefSupervisor';
import { ChiefSupervisorContent } from './ChiefSupervisorContent';
import { FUNCTION_HUB_TEXT } from '../lang';
import { type AlertDetails, Button } from '../../../../../../components';
import { useDeclineNomination } from '../../../../../Nomination/graphql';
import { useAuth } from '../../../../../../contexts';
import { useRejectNonProposedApplication } from '../../../../graphql';
import { type RawFundingWithStatus } from '../../../../../Application/components/ApplicationViewList/graphql';

type DeclineApplicationDialogProps = {
  applicationId: string;
  nominationId: string;
  functionMode: FUNCTION_MODE;
  fundingDetails: RawFundingWithStatus[];
  departmentLevel: boolean;
  handleOpenAlert: (alertDetails: AlertDetails) => void;
  handleCloseDialog: () => void;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

export const ManageChiefSupervisorDialog: React.FC<
  DeclineApplicationDialogProps
> = (props: DeclineApplicationDialogProps) => {
  const {
    applicationId,
    nominationId,
    functionMode,
    fundingDetails,
    departmentLevel,
    handleCloseDialog,
    handleOpenAlert,
    refetch,
  } = props;
  const { handleLoading } = useAuth();
  const declineNomination = useDeclineNomination();
  const handleDeclineNomination = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await declineNomination({
          variables: {
            applicationId,
          },
        });
        await refetch();
        handleOpenAlert({
          alertContent: 'Declined the Nomination Successfully.',
          alertType: 'success',
        });
      } catch (e) {
        handleOpenAlert({
          alertContent:
            'Declined the Nomination Failed.' + (e as Error).message,
          alertType: 'error',
        });
      }
    });
  };

  const rejectApplication = useRejectNonProposedApplication();

  const handleRejectApplication = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        await rejectApplication({
          variables: {
            applicationIds: [applicationId],
          },
        });
        handleOpenAlert({
          alertContent: 'Rejected the Application Successfully',
          alertType: 'success',
        });
        await refetch();
      } catch (error) {
        handleOpenAlert({
          alertContent:
            'Failed to Reject the Application.' + (error as Error)?.message,
          alertType: 'error',
        });
      }
    });
  };

  const contentMap: Record<
    FUNCTION_MODE,
    {
      title: string;
      content: React.ReactNode;
      confirmButton: string | null;
      confirmAction?: () => Promise<void>;
    }
  > = {
    [FUNCTION_MODE.CHANGE_SUPERVISOR]: {
      title: FUNCTION_HUB_TEXT.CHANGE_SUPERVISOR.TITLE,
      content: (
        <ChiefSupervisorContent
          nominationId={nominationId}
          fundingDetails={fundingDetails}
          departmentLevel={departmentLevel}
          refetch={refetch}
          handleOpenAlert={handleOpenAlert}
          handleCloseDialog={handleCloseDialog}
        />
      ),
      confirmButton: null,
    },
    [FUNCTION_MODE.DECLINE_APPLICATION]: {
      title: FUNCTION_HUB_TEXT.DECLINE_APPLICATION.TITLE,
      content: (
        <div className="flex flex-row text-[14px] gap-[24px] items-end">
          <ErrorIcon
            sx={{ width: '44px', height: '44px', marginBottom: '25px' }}
            color="primary"
          />
          <div>
            <p>{FUNCTION_HUB_TEXT.DECLINE_APPLICATION.QUESTION}</p>
            <p className="text-[#951D1D] italic mt-[10px]">
              {FUNCTION_HUB_TEXT.DECLINE_APPLICATION.NOTE}
            </p>
          </div>
        </div>
      ),
      confirmButton: FUNCTION_HUB_TEXT.CONFIRM,
      confirmAction: handleDeclineNomination,
    },
    [FUNCTION_MODE.REJECT_APPLICATION]: {
      title: FUNCTION_HUB_TEXT.REJECT_APPLICATION.TITLE,
      content: (
        <div className="flex flex-row text-[14px] gap-[24px] items-end !pt-[20px]">
          <ErrorIcon
            sx={{ width: '44px', height: '44px', marginBottom: '5px' }}
            color="primary"
          />
          <div>
            <p>{FUNCTION_HUB_TEXT.REJECT_APPLICATION.QUESTION}</p>
            <p className="text-[#951D1D] italic mt-[10px]">
              {FUNCTION_HUB_TEXT.REJECT_APPLICATION.NOTE}
            </p>
          </div>
        </div>
      ),
      confirmButton: FUNCTION_HUB_TEXT.CONFIRM,
      confirmAction: handleRejectApplication,
    },
  };
  return (
    <>
      <div className="min-w-[700px] px-[28px] py-[20px] bg-[#FAFAFA]">
        <div className="text-[#951D1D] text-[14px] font-bold">
          {contentMap[functionMode].title}
        </div>
        <div>{contentMap[functionMode].content}</div>
        {contentMap[functionMode].confirmButton !== null && (
          <div className="flex flex-row justify-center gap-[20px] mt-4">
            <Button
              variant="contained"
              className="!normal-case"
              onClick={contentMap[functionMode].confirmAction}
            >
              {contentMap[functionMode].confirmButton}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              className="!normal-case"
            >
              {FUNCTION_HUB_TEXT.CANCEL}
            </Button>
          </div>
        )}
      </div>
    </>
  );
};
